import React from 'react';
import { AppPermissions } from '../../../primary-record-types/abac';
import { GroupNode as GroupNodeType, LeafNode as LeafNodeType, Node as NodeType } from './types';
import {
    evaluateEditValue,
    evaluateViewValue,
    getShouldDisableEditFromMostRestrictiveAppPermissions,
    getShouldDisableViewFromMostRestrictiveAppPermissions,
    updateSubPermissions,
} from './utils';
import { permissionsIntl } from './intl';
import { ConfigurableTableRow } from '../../v2BaseComponents/ConfigurationTable';

export type GroupNodeOwnProps = {
    value: AppPermissions;
    onChange: (value: AppPermissions) => void;
    mostPermissiveAppPermissions?: AppPermissions;
    node: GroupNodeType;
    isDisabled?: boolean;
};

export type GroupNodeProps = GroupNodeOwnProps;

const GroupNode: React.FC<GroupNodeProps> = ({ value, onChange, node, isDisabled, mostPermissiveAppPermissions }) => {
    const viewValue = evaluateViewValue(node, value);
    const editValue = evaluateEditValue(node, value);
    const handleViewChange = (checkboxValue: boolean) => {
        const newValue = updateSubPermissions(node, checkboxValue === true ? 'view' : 'denied', value);
        onChange(newValue);
    };
    const disableViewFromRestrictiveAppPermissions = getShouldDisableViewFromMostRestrictiveAppPermissions(
        node,
        mostPermissiveAppPermissions,
    );

    const handleEditChange = (switchValue: boolean) => {
        const newValue = updateSubPermissions(node, switchValue === true ? 'edit' : 'view', value);
        onChange(newValue);
    };
    const disableEditFromRestrictiveAppPermissions = getShouldDisableEditFromMostRestrictiveAppPermissions(
        node,
        mostPermissiveAppPermissions,
    );

    return (
        <>
            <ConfigurableTableRow
                title={permissionsIntl(node.nodeType)}
                checkboxes={[
                    {
                        onToggle: handleViewChange,
                        value: viewValue === 'maybe' ? 'indeterminate' : viewValue === true,
                        isDisabled: isDisabled || disableViewFromRestrictiveAppPermissions,
                        testID: `view-checkbox-${node.nodeType}`,
                    },
                    {
                        onToggle: handleEditChange,
                        value: editValue === true,
                        isDisabled: editValue === 'disabled' || disableEditFromRestrictiveAppPermissions,
                        testID: `edit-checkbox-${node.nodeType}`,
                    },
                ]}
                isGroupRow
            />
            {node.subNodes.map((subNode, index) => (
                <Node
                    key={index}
                    value={value}
                    onChange={onChange}
                    node={subNode}
                    isDisabled={isDisabled}
                    isRoot={false}
                    isLast={index === node.subNodes.length - 1}
                    mostPermissiveAppPermissions={mostPermissiveAppPermissions}
                />
            ))}
        </>
    );
};

export type LeafNodeOwnProps = {
    value: AppPermissions;
    onChange: (value: AppPermissions) => void;
    node: LeafNodeType;
    isDisabled?: boolean;
    isRoot: boolean;
    isLast: boolean;
    mostPermissiveAppPermissions?: AppPermissions;
};

export type LeafNodeProps = LeafNodeOwnProps;

const LeafNode: React.FC<LeafNodeProps> = ({
    node,
    value,
    onChange,
    isDisabled,
    isRoot,
    isLast,
    mostPermissiveAppPermissions,
}) => {
    const viewValue = evaluateViewValue(node, value);
    const disableViewFromRestrictiveAppPermissions = getShouldDisableViewFromMostRestrictiveAppPermissions(
        node,
        mostPermissiveAppPermissions,
    );

    const editValue = evaluateEditValue(node, value);
    const disableEditFromRestrictiveAppPermissions = getShouldDisableEditFromMostRestrictiveAppPermissions(
        node,
        mostPermissiveAppPermissions,
    );

    const handleViewChange = (checkboxValue: boolean) => {
        const newValue = updateSubPermissions(node, checkboxValue === true ? 'view' : 'denied', value);
        onChange(newValue);
    };

    const handleEditChange = (switchValue: boolean) => {
        const newValue = updateSubPermissions(node, switchValue === true ? 'edit' : 'view', value);
        onChange(newValue);
    };

    return (
        <ConfigurableTableRow
            title={permissionsIntl(node.permission)}
            checkboxes={[
                {
                    onToggle: handleViewChange,
                    value: viewValue === 'maybe' ? 'indeterminate' : viewValue === true,
                    isDisabled: isDisabled || disableViewFromRestrictiveAppPermissions,
                    testID: `view-checkbox-${node.permission}`,
                },
                {
                    onToggle: handleEditChange,
                    value: editValue === true,
                    isDisabled: editValue === 'disabled' || disableEditFromRestrictiveAppPermissions,
                    testID: `edit-checkbox-${node.permission}`,
                },
            ]}
            isLastInGroupRow={isLast}
            isGroupRow={isRoot}
        />
    );
};

export type NodeOwnProps = {
    value: AppPermissions;
    onChange: (value: AppPermissions) => void;
    mostPermissiveAppPermissions?: AppPermissions;
    node: NodeType;
    isDisabled?: boolean;
    isRoot: boolean;
    isLast: boolean;
};

export type NodeProps = NodeOwnProps;

const Node: React.FC<NodeProps> = (props) => {
    return props.node.type === 'group-node' ? (
        <GroupNode {...props} node={props.node as GroupNodeType} />
    ) : (
        <LeafNode {...props} node={props.node as LeafNodeType} isRoot={props.isRoot} isLast={props.isLast} />
    );
};

export default React.memo(Node);
